/**
 * Urban pathway calculation functions for the Cities & Communities Model.
 *
 * Each function computes a pathway score from urban indicators:
 *   Positive pathways: 0 to 1
 *   Negative pathways: -1 to 0
 */

import * as p from './parameters';

export function clamp(value, minVal = 0.0, maxVal = 1.0) {
  return Math.max(minVal, Math.min(maxVal, value));
}

// ── Helper: smooth scoring curve ──

function scoreCurve(value, min, good, optimal) {
  if (value >= optimal) return 1.0;
  if (value >= good) return 0.7 + 0.3 * (value - good) / (optimal - good);
  if (value >= min) return 0.4 + 0.3 * (value - min) / (good - min);
  if (value > 0) return 0.4 * (value / min);
  return 0.0;
}

// ── Positive Pathways ──

export function calculateMovement(walkScore, bikeInfrastructure, blockPermeability) {
  const params = p.MOVEMENT;
  const walkComponent = scoreCurve(walkScore, params.walkScore_min, params.walkScore_good, params.walkScore_optimal) * 0.50;
  const bikeComponent = Math.min(1.0, bikeInfrastructure / params.bikeInfra_optimal) * 0.25;
  const permeabilityComponent = Math.min(1.0, blockPermeability / params.blockPermeability_optimal) * 0.25;
  return clamp(walkComponent + bikeComponent + permeabilityComponent);
}

export function calculateNature(treeCanopyPct, greenSpaceAccess300m, permeableSurfacePct) {
  const params = p.NATURE;
  const canopyComponent = scoreCurve(treeCanopyPct, params.treeCanopy_min_pct, params.treeCanopy_good_pct, params.treeCanopy_optimal_pct) * 0.45;
  const accessComponent = scoreCurve(greenSpaceAccess300m, 30, params.greenSpaceAccess_good_pct, params.greenSpaceAccess_optimal_pct) * 0.35;
  const permeableComponent = scoreCurve(permeableSurfacePct, 10, params.permeableSurface_good_pct, params.permeableSurface_optimal_pct) * 0.20;
  return clamp(canopyComponent + accessComponent + permeableComponent);
}

export function calculateDaylight(skyViewFactor, sunlightHoursGroundPlane) {
  const params = p.DAYLIGHT;
  const svfComponent = scoreCurve(skyViewFactor, params.skyViewFactor_min, params.skyViewFactor_good, params.skyViewFactor_optimal) * 0.50;
  const sunComponent = scoreCurve(sunlightHoursGroundPlane, params.sunlightHours_min, params.sunlightHours_good, params.sunlightHours_optimal) * 0.50;
  return clamp(svfComponent + sunComponent);
}

export function calculateEnrichment(mixedUseIndex, groundPlaneActivationPct, socialInfraPerThousand) {
  const params = p.ENRICHMENT;
  const mixedUseComponent = scoreCurve(mixedUseIndex, 0.2, params.mixedUse_good, params.mixedUse_optimal) * 0.35;
  const activationComponent = scoreCurve(groundPlaneActivationPct, 20, params.groundPlaneActivation_good_pct, params.groundPlaneActivation_optimal_pct) * 0.35;
  const socialComponent = scoreCurve(socialInfraPerThousand, 0.5, params.socialInfra_good_per1k, params.socialInfra_optimal_per1k) * 0.30;
  return clamp(mixedUseComponent + activationComponent + socialComponent);
}

export function calculateWayfinding(streetIntelligibility, landmarkScore) {
  const params = p.WAYFINDING;
  const intellComponent = scoreCurve(streetIntelligibility, 0.2, params.intelligibility_good, params.intelligibility_optimal) * 0.55;
  const landmarkComponent = scoreCurve(landmarkScore, 0.2, params.landmark_good, params.landmark_optimal) * 0.45;
  return clamp(intellComponent + landmarkComponent);
}

// ── Negative Pathways ──

export function calculateAirQuality(pm25Annual, pctZoneOver100mFromTraffic, greenBufferScore) {
  const params = p.AIR_QUALITY;

  // PM2.5 impact
  let pmScore = 0;
  if (pm25Annual <= params.pm25_who_guideline) {
    pmScore = 0;
  } else if (pm25Annual <= params.pm25_acceptable) {
    pmScore = -0.3 * (pm25Annual - params.pm25_who_guideline) / (params.pm25_acceptable - params.pm25_who_guideline);
  } else if (pm25Annual <= params.pm25_unhealthy) {
    pmScore = -0.3 - 0.4 * (pm25Annual - params.pm25_acceptable) / (params.pm25_unhealthy - params.pm25_acceptable);
  } else {
    pmScore = -0.7 - 0.3 * Math.min(1, (pm25Annual - params.pm25_unhealthy) / 35);
  }

  // Traffic buffer benefit (reduces penalty)
  const bufferBenefit = (pctZoneOver100mFromTraffic / 100) * 0.15;

  // Green buffer benefit
  const greenBenefit = greenBufferScore * 0.10;

  return clamp(pmScore + bufferBenefit + greenBenefit, -1.0, 0.0);
}

export function calculateNoise(pctZoneOver65dB, pctQuietAreas, soundEnvironmentQuality) {
  const params = p.NOISE;

  // Noise exposure penalty
  let noisePenalty = 0;
  if (pctZoneOver65dB <= 10) {
    noisePenalty = 0;
  } else if (pctZoneOver65dB <= params.pctOver65dB_max) {
    noisePenalty = -0.3 * (pctZoneOver65dB - 10) / (params.pctOver65dB_max - 10);
  } else if (pctZoneOver65dB <= params.pctOver65dB_concern) {
    noisePenalty = -0.3 - 0.3 * (pctZoneOver65dB - params.pctOver65dB_max) / (params.pctOver65dB_concern - params.pctOver65dB_max);
  } else {
    noisePenalty = -0.6 - 0.4 * Math.min(1, (pctZoneOver65dB - params.pctOver65dB_concern) / 50);
  }

  // Quiet area benefit
  const quietBenefit = Math.min(0.2, (pctQuietAreas / 100) * 0.25);

  // Sound quality benefit
  const qualityBenefit = soundEnvironmentQuality * 0.1;

  return clamp(noisePenalty + quietBenefit + qualityBenefit, -1.0, 0.0);
}

export function calculateVisualEnvironment(facadeDiversityIndex, streetscapeVisualInterest) {
  const params = p.VISUAL;

  // Low diversity = monotonous = stress
  const diversityGap = Math.max(0, params.facadeDiversity_good - facadeDiversityIndex);
  const interestGap = Math.max(0, params.streetscapeInterest_good - streetscapeVisualInterest);

  let penalty = 0;
  if (diversityGap > 0) {
    penalty -= diversityGap * 0.8;
  }
  if (interestGap > 0) {
    penalty -= interestGap * 0.6;
  }

  // Good visual environment provides positive contribution if both are high
  const avg = (facadeDiversityIndex + streetscapeVisualInterest) / 2;
  if (avg >= params.facadeDiversity_good) {
    // Positive contribution — visual richness supports cognitive engagement
    return clamp(penalty, -1.0, 0.0);
  }

  return clamp(penalty, -1.0, 0.0);
}

// ── Synergy ──

export function calculatePathwaySynergy(pathwayScores) {
  const sp = p.SYNERGY;
  let synergy = 0;

  const nature = pathwayScores.nature || 0;
  const movement = pathwayScores.movement || 0;
  const enrichment = pathwayScores.enrichment || 0;

  // Nature + Movement synergy (active green spaces)
  if (nature > sp.nature_movement_threshold && movement > sp.nature_movement_threshold) {
    synergy += (sp.nature_movement_multiplier - 1.0) * Math.min(nature, movement);
  }

  // Mixed-use + Walkability synergy (vibrant neighborhoods)
  if (enrichment > sp.mixeduse_walkability_threshold && movement > sp.mixeduse_walkability_threshold) {
    synergy += (sp.mixeduse_walkability_multiplier - 1.0) * Math.min(enrichment, movement);
  }

  // Triple synergy (nature + movement + enrichment)
  if (nature >= sp.triple_threshold && movement >= sp.triple_threshold && enrichment >= sp.triple_threshold) {
    synergy += sp.triple_bonus;
  }

  return clamp(synergy, 0.0, 0.25);
}

// ── Regional Scores ──

export function calculateRegionalScores(pathwayScores) {
  const dorsalWeights = p.REGIONAL_WEIGHTS.dorsal;
  const ventralWeights = p.REGIONAL_WEIGHTS.ventral;
  const positivePathways = ['movement', 'nature', 'daylight', 'enrichment', 'wayfinding'];

  let dorsalScore = 0;
  let ventralScore = 0;

  for (const pw of positivePathways) {
    const s = pathwayScores[pw] || 0;
    if (s > 0) {
      dorsalScore += s * (dorsalWeights[pw] || 0);
      ventralScore += s * (ventralWeights[pw] || 0);
    }
  }

  const negativeImpact = ['noise', 'air_quality', 'visual']
    .reduce((sum, pw) => sum + (pathwayScores[pw] || 0), 0);

  dorsalScore = clamp(dorsalScore + negativeImpact * 0.3);
  ventralScore = clamp(ventralScore + negativeImpact * 0.35);

  return { dorsal: dorsalScore, ventral: ventralScore };
}
